import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Reveal, SectionLabel, EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT = [
  {
    icon: Phone,
    label: "Phone",
    value: "0410 514 499",
    href: "tel:0410514499",
    testid: "contact-phone-link",
  },
  {
    icon: Mail,
    label: "Email",
    value: "afterpaint@hotmail.com",
    href: "mailto:afterpaint@hotmail.com",
    testid: "contact-email-link",
  },
  {
    icon: MapPin,
    label: "Service area",
    value: "Byron Shire to Coffs Coast",
    testid: "contact-area",
  },
];

const FIELD_CLASS =
  "rounded-none bg-transparent border-white/15 text-bone placeholder:text-smoke/60 focus-visible:ring-1 focus-visible:ring-ember focus-visible:ring-offset-0 focus-visible:border-ember transition-colors duration-300";

export default function QuoteSection() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", settlement: "", details: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSent(true);
      toast.success("Quote request sent — Craig will be in touch.");
    } catch (err) {
      toast.error("Something went wrong — please call 0410 514 499 instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-ash border-y border-line"
    >
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <Reveal>
            <SectionLabel>Free Quote</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-8 leading-[1.05]">
              Selling your home?
              <br />
              <span className="italic text-smoke">Let's get it market-ready.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-smoke font-light leading-relaxed mt-8 max-w-md text-base md:text-lg">
              Every quote is free and honest — and with paint now, pay later
              there's nothing to pay until settlement. Send the details below,
              or pick up the phone — you'll speak to the painter, not a call
              centre.
            </p>
          </Reveal>
          <div className="mt-14">
            {CONTACT.map((c, i) => (
              <Reveal key={c.label} delay={0.25 + i * 0.08}>
                <div className="flex items-center gap-6 py-6 border-t border-line last:border-b">
                  <c.icon size={18} strokeWidth={1.25} className="text-ember shrink-0" />
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-1">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-smoke">
                      {c.label}
                    </span>
                    {c.href ? (
                      <a
                        data-testid={c.testid}
                        href={c.href}
                        className="text-bone font-light hover:text-ember transition-colors duration-300"
                      >
                        {c.value}
                      </a>
                    ) : (
                      <span data-testid={c.testid} className="text-bone font-light">
                        {c.value}
                      </span>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.2} y={48}>
          <div className="bg-coal border border-line p-8 md:p-12">
            {sent ? (
              <motion.div
                data-testid="quote-success-panel"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="flex flex-col items-start gap-6 py-10"
              >
                <span className="flex items-center justify-center w-14 h-14 border border-ember">
                  <Check size={22} className="text-ember" strokeWidth={1.5} />
                </span>
                <h3 className="font-serif font-light text-3xl md:text-4xl tracking-tight">
                  Request received.
                </h3>
                <p className="text-smoke font-light leading-relaxed">
                  Thanks, {form.name.split(" ")[0] || "friend"} — Craig will
                  call you back on {form.phone || "your number"} to talk through
                  the job. Usually within a day.
                  {form.email ? " A confirmation is on its way to your inbox too." : ""}
                </p>
                <button
                  data-testid="quote-send-another-button"
                  onClick={() => {
                    setSent(false);
                    setForm({ name: "", phone: "", email: "", settlement: "", details: "" });
                  }}
                  className="text-xs uppercase tracking-[0.25em] font-semibold text-ember hover:text-bone transition-colors duration-300"
                >
                  Send another request
                </button>
              </motion.div>
            ) : (
              <form data-testid="quote-form" onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <label
                    htmlFor="quote-name"
                    className="block text-[10px] uppercase tracking-[0.3em] text-smoke mb-3"
                  >
                    Your name
                  </label>
                  <Input
                    id="quote-name"
                    data-testid="quote-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Jane Citizen"
                    className={FIELD_CLASS}
                  />
                </div>
                <div>
                  <label
                    htmlFor="quote-phone"
                    className="block text-[10px] uppercase tracking-[0.3em] text-smoke mb-3"
                  >
                    Phone
                  </label>
                  <Input
                    id="quote-phone"
                    data-testid="quote-phone-input"
                    required
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="04XX XXX XXX"
                    className={FIELD_CLASS}
                  />
                </div>
                <div>
                  <label
                    htmlFor="quote-email"
                    className="block text-[10px] uppercase tracking-[0.3em] text-smoke mb-3"
                  >
                    Email <span className="text-smoke/50">(optional — for a confirmation)</span>
                  </label>
                  <Input
                    id="quote-email"
                    data-testid="quote-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    className={FIELD_CLASS}
                  />
                </div>
                <div>
                  <label
                    htmlFor="quote-settlement"
                    className="block text-[10px] uppercase tracking-[0.3em] text-smoke mb-3"
                  >
                    Settlement date <span className="text-smoke/50">(if known)</span>
                  </label>
                  <Input
                    id="quote-settlement"
                    data-testid="quote-settlement-input"
                    value={form.settlement}
                    onChange={(e) => setForm({ ...form, settlement: e.target.value })}
                    placeholder="e.g. Mid-October — or not sure yet"
                    className={FIELD_CLASS}
                  />
                </div>
                <div>
                  <label
                    htmlFor="quote-details"
                    className="block text-[10px] uppercase tracking-[0.3em] text-smoke mb-3"
                  >
                    The job
                  </label>
                  <Textarea
                    id="quote-details"
                    data-testid="quote-details-input"
                    required
                    rows={5}
                    value={form.details}
                    onChange={(e) => setForm({ ...form, details: e.target.value })}
                    placeholder="e.g. Selling our three-bedder — exterior and lounge need freshening up before the photos."
                    className={`${FIELD_CLASS} resize-none`}
                  />
                </div>
                <motion.button
                  data-testid="quote-submit-button"
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  disabled={sending}
                  className="w-full bg-ember text-coal text-xs uppercase tracking-[0.25em] font-semibold py-5 rounded-full hover:bg-ember-dark disabled:opacity-60 transition-colors duration-300"
                >
                  {sending ? "Sending…" : "Request My Free Quote"}
                </motion.button>
                <p className="text-[11px] text-smoke/70 font-light tracking-wide">
                  No spam, no obligation. Your details go straight to Craig.
                </p>
                <p
                  data-testid="quote-compliance"
                  className="text-[11px] text-smoke/50 font-light tracking-wide"
                >
                  Licensed painter — Lic. 205444C · ABN 12 474 073 126
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
